import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Card } from '@material-ui/core';
import PropTypes from 'prop-types';
import { compact, isEmpty } from 'lodash';
import get from 'lodash/get';
import Divider from '@material-ui/core/Divider';
import { withTranslation } from '../../../src/i18n';
import { colors } from '../../../constants/styleConstants';
import Title from '../../../components/Title';
import { EMPTY_INFO_MARK, TP } from '../../../constants/consts';
import CheckboxField from '../../../components/Fields/CheckboxField';
import PerformedDates from '../../../components/EditComponents/PerformedDates';
import { getGroupedProductionsDateForTheCard, dateSendFormat } from '../../../utils/common';
const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    border: `1px solid ${colors.linesGray}`,
    padding: theme.spacing(2),
    backgroundColor: colors.background.grayLight,
    marginBottom: theme.spacing(2),
  },
  datesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  monthWrapper: {
    display: 'flex',
  },
  datesDivider: {
    margin: '0 7px 0 4px',
  },
}));

const SelectDatesCard = ({ t, dates, onDateClick, roles, selectedDates }) => {
  const classes = useStyles();
  const [groupedDates, setDates] = React.useState({});
  const [convertedDates, setConvertedDates] = React.useState({});
  const isAllDatesChecked = selectedDates?.length === dates?.length && selectedDates?.length !== 0;

  React.useEffect(() => {
    setConvertedDates(dates.map(d => dateSendFormat(d)));
  }, [dates]);

  React.useEffect(() => {
    setDates(
      getGroupedProductionsDateForTheCard({ dates: dates.map(d => ({ date: dateSendFormat(d) })) }, false, date =>
        selectedDates.includes(date?.date),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, selectedDates]);

  const updatePerformedDate = date => {
    const dateValue = dateSendFormat(date?.date);
    onDateClick([dateValue], !selectedDates.includes(dateValue)); // if date was selected now
  };

  const selectAllDates = isSelectedAll => {
    onDateClick(convertedDates, isSelectedAll);
  };

  const rolesToDisplay = React.useCallback(
    () =>
      compact(
        roles?.map(role =>
          role?.type === 'role'
            ? `${role?.workRole?.name || role?.workRole?.original_name || role?.workRoleReference || '~'} ${
                role?.professionQualification?.id ? `(${role?.professionQualification?.name})` : ''
              }`
            : role?.profession?.name || role?.professionReference,
        ),
      ).join(', '),
    [roles],
  );

  return (
    <Card className={classes.root}>
      <Title>{rolesToDisplay()}</Title>
      <CheckboxField
        label={t(`${TP}.FN_SELECT_ALL_DATES`)}
        onChange={selectAllDates}
        checked={isAllDatesChecked}
        key={`${selectedDates?.length}-${dates?.length}`} // to update state
      />
      {isEmpty(dates) ? (
        EMPTY_INFO_MARK
      ) : (
        <div className={classes.datesContainer}>
          {Object.keys(groupedDates).map((key, index) => (
            <div key={`${key}-${index}`} className={classes.monthWrapper}>
              <PerformedDates title={key} dates={get(groupedDates, key, [])} onDateClick={updatePerformedDate} />
              {index + 1 !== Object.keys(dates).length && (
                <Divider orientation="vertical" flexItem classes={{ root: classes.datesDivider }} />
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

SelectDatesCard.propTypes = {
  t: PropTypes.func,
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
    }),
  ).isRequired,
  onDateClick: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  selectedDates: PropTypes.array,
};

SelectDatesCard.defaultProps = {
  selectedDates: [],
};

export default withTranslation('translation')(SelectDatesCard);
